import {
  Badge,
  Button,
  SearchIcon,
  Stack,
  Table,
  TableCell,
  TablePager,
  TableRow,
} from '@fukuri-admins/ui/common';
import { TextInput } from '@fukuri-admins/ui/common/Form/FormItems';
import styles from './styles.module.scss';

/**
 * 種別 Badge の表示設定
 * - 'standard' → 「標準」グレー（neutral）
 * - 'custom' → 「個社」青（info）
 */
type KindBadge = {
  /** Badge ラベル */
  label: string;
  /** Badge ステータス */
  status: 'neutral' | 'info';
};

/**
 * 契約プランテーブルの行データ型
 *
 * 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type ContractPlanRow = {
  /** 契約プラン ID（React key 用） */
  id: number;
  /** 契約プラン名 */
  name: string;
  /** 種別 Badge 表示用データ */
  kindBadge: KindBadge;
  /**
   * ギフトプラン表示テキスト
   *
   * ギフトプランあり: 「{amount}円（{months_interval}ヶ月に1回 利用可）」
   * ギフトプランなし: 「-」
   */
  giftPlan: string;
  /** 顧客請求単価（表示整形済み：「{amount}円」形式） */
  customerChargeUnitPrice: string;
  /** 税率（表示整形済み：「{percent}%」形式） */
  taxRatePercent: string;
  /** 作成日時（表示整形済み：「yyyy/MM/dd HH:mm」形式） */
  createdAt: string;
};

type Props = {
  /** テーブルに表示する契約プラン行データ */
  rows: ContractPlanRow[];
  /** ページャー props */
  pagerProps: React.ComponentProps<typeof TablePager>;
  /**
   * 行クリック時のコールバック（契約プラン詳細画面への遷移）
   *
   * @param planId 操作対象の契約プラン ID
   */
  onRowClick: (planId: number) => void;
  /** 検索 Input の現在値 */
  searchValue: string;
  /**
   * 検索 Input の値変更時のコールバック
   *
   * @param value 入力値
   */
  onSearchChange: (value: string) => void;
  /** 検索ボタン押下時のコールバック */
  onSearch: () => void;
  /** 検索クリアボタン（×）押下時のコールバック */
  onSearchClear: () => void;
};

/** テーブルヘッダー定義（6 列） */
const TABLE_HEADERS: React.ComponentProps<typeof Table>['headers'] = [
  { type: 'text', label: '契約プラン' },
  { type: 'text', label: '種別', className: styles.kindCol },
  { type: 'text', label: 'ギフトプラン' },
  { type: 'text', label: '顧客請求単価', className: styles.priceCol },
  { type: 'text', label: '税率', className: styles.taxCol },
  { type: 'text', label: '作成日時', className: styles.colDateTime },
];

/**
 * 契約プラン一覧 Presentation
 *
 * - 6 列（契約プラン / 種別 / ギフトプラン / 顧客請求単価 / 税率 / 作成日時）
 * - 廃止プランを含む全プランを表示する（PartnerAdmin の方針）
 * - TablePager でページネーション操作バーを表示する
 * - リスト表示専用。Empty 表示の出し分けは Container 層で行う
 */
export const ContractPlanListPresentation: React.FC<Props> = ({
  rows,
  pagerProps,
  onRowClick,
  searchValue,
  onSearchChange,
  onSearch,
  onSearchClear,
}) => (
  <Stack direction='column' gap={16}>
    <Stack direction='row' alignItems='center' gap={8}>
      <div className={styles.searchInputWrapper}>
        <TextInput
          placeholder='プラン名で検索'
          startIcon={<SearchIcon size='sm' />}
          value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
          onClear={onSearchClear}
        />
      </div>
      <Button variant='secondary' size='md' onClick={onSearch}>
        検索
      </Button>
    </Stack>
    <Table headers={TABLE_HEADERS} scrollable>
      {rows.map(row => (
        <TableRow key={row.id} onClick={() => onRowClick(row.id)}>
          <TableCell>{row.name}</TableCell>
          <TableCell className={styles.kindCell}>
            <Badge
              status={row.kindBadge.status}
              variant='outline'
              size='sm'
              label={row.kindBadge.label}
            />
          </TableCell>
          <TableCell>{row.giftPlan}</TableCell>
          <TableCell>{row.customerChargeUnitPrice}</TableCell>
          <TableCell>{row.taxRatePercent}</TableCell>
          <TableCell>{row.createdAt}</TableCell>
        </TableRow>
      ))}
    </Table>
    <TablePager {...pagerProps} />
  </Stack>
);
